import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import Api from "../../../../../../../../core/services/api/api";
import Grid2 from "@mui/material/Unstable_Grid2";
import {Box, Card, Divider, Tooltip, Typography} from "@mui/material";
import {InfoOutlined} from "@mui/icons-material";
import Form, {MuiRFF, Yup} from "../../../../../../../components/form";
import ValidateMessages from "../../../../../../../../core/models/static/texts/vallidate-messages";
import Button from "../../../../../../../components/base/button/button";
import {deepEqual} from "../../../../../../../../core/services/utils/utils";

export const AccountingSystemCredentialsConfigurationsSection = () => {
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const isMounted = useIsMounted();
    const [initialValues, setInitialValues] = useState({
        companyName: "",
        baseUrl: "",
        username: "",
        password: ""
    });

    const schema = useMemo(() => {
        return Yup.object().shape({
            companyName: Yup.string().required(ValidateMessages.required),
            baseUrl: Yup.string().url(ValidateMessages.incorrect).required(ValidateMessages.required),
            username: Yup.string().required(ValidateMessages.required),
            password: Yup.string().required(ValidateMessages.required),
        });
    }, []);

    const validate = useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    /**
     * Fetches the Spire connection configurations from the server.
     */
    const getConfigurations = useCallback(async () => {
        setLoading(true);
        const response = await Api.getSpireConnection();
        if (!isMounted()) return;
        setLoading(false);
        if (response?.resultFlag) {
            setInitialValues({
                companyName: response.data?.companyName ?? "",
                baseUrl: response.data?.baseUrl ?? "",
                username: response.data?.username ?? "",
                password: "" // Password is never returned by the API
            });
        }
    }, [isMounted]);

    /**
     * Fetches the data on mount.
     */
    useEffect(() => {
        void getConfigurations();
    }, [getConfigurations]);

    /**
     * Updates the Spire connection settings.
     */
    const onSubmit = useCallback(async (values, form) => {
        if (deepEqual(values, initialValues)) {
            return {resultFlag: true};
        }
        setSubmitting(true);
        const response = await Api.updateSpireConnection(values);
        if (!isMounted()) return;
        setSubmitting(false);
        if (response?.resultFlag) {
            const nextInitialValues = {
                companyName: values.companyName,
                baseUrl: values.baseUrl,
                username: values.username,
                password: "",
            };
            setInitialValues(nextInitialValues);
            form.restart(nextInitialValues);
        }
        return response;
    }, [initialValues, isMounted]);

    if (loading) {
        return (
            <Grid2 xs={12}>
                <div className={'loading access-level w-100'}>
                    <div>
                        <div/>
                    </div>
                </div>
            </Grid2>
        );
    }

    return (
        <Grid2 xs={12}>
            <Card variant={'elevation'} sx={{boxShadow: 'none', backgroundImage: 'none', padding: 2, width: '100%'}}>
                <Form
                    onSubmit={onSubmit}
                    validate={validate}
                    initialValues={initialValues}
                    formProps={{id: 'accounting-system-credentials-form'}}
                >
                    {({values}) => {
                        const hasChanges = !deepEqual(values, initialValues);
                        return (
                            <Grid2 container spacing={2}>
                                <Grid2 xs={12}>
                                    <Typography variant={'subtitle1'} fontWeight={600}>
                                        Accounting System Credentials
                                    </Typography>
                                </Grid2>
                                <Grid2 xs={12}>
                                    <Divider variant={'fullWidth'} orientation={'horizontal'}/>
                                </Grid2>

                                <Grid2 xs={12} lg={6}>
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
                                        <Typography variant="body2" fontWeight={500}>Company Name</Typography>
                                        <Tooltip title={'The name of the company in Accounting service.'}>
                                            <InfoOutlined color={'primary'} sx={{fontSize: 18}}/>
                                        </Tooltip>
                                    </Box>
                                    <MuiRFF.TextField
                                        name="companyName"
                                        placeholder='e.g. My Company'
                                        required={required.companyName}
                                        fullWidth
                                        disabled={submitting}
                                    />
                                </Grid2>

                                <Grid2 xs={12} lg={6}>
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
                                        <Typography variant="body2" fontWeight={500}>Base URL</Typography>
                                        <Tooltip title={'The API base URL for your Accounting service instance.'}>
                                            <InfoOutlined color={'primary'} sx={{fontSize: 18}}/>
                                        </Tooltip>
                                    </Box>
                                    <MuiRFF.TextField
                                        name="baseUrl"
                                        placeholder='https://spire.example.com'
                                        required={required.baseUrl}
                                        fullWidth
                                        disabled={submitting}
                                    />
                                </Grid2>

                                <Grid2 xs={12} lg={6}>
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
                                        <Typography variant="body2" fontWeight={500}>Username</Typography>
                                        <Tooltip title={'The username used to connect to Accounting service.'}>
                                            <InfoOutlined color={'primary'} sx={{fontSize: 18}}/>
                                        </Tooltip>
                                    </Box>
                                    <MuiRFF.TextField
                                        name="username"
                                        placeholder='e.g. admin'
                                        required={required.username}
                                        fullWidth
                                        disabled={submitting}
                                    />
                                </Grid2>

                                <Grid2 xs={12} lg={6}>
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
                                        <Typography variant="body2" fontWeight={500}>Password</Typography>
                                        <Tooltip title={'The password used to connect to Accounting service. This field is mandatory for any updates.'}>
                                            <InfoOutlined color={'primary'} sx={{fontSize: 18}}/>
                                        </Tooltip>
                                    </Box>
                                    <MuiRFF.TextField
                                        name="password"
                                        type="password"
                                        placeholder='Enter password to update'
                                        required={required.password}
                                        fullWidth
                                        disabled={submitting}
                                    />
                                </Grid2>

                                <Grid2 xs={12} sx={{display: 'flex', justifyContent: 'flex-end', mt: 2}}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        disabled={submitting || !hasChanges}
                                        form="accounting-system-credentials-form"
                                        sx={{minWidth: 200}}
                                    >
                                        {submitting ? 'Saving...' : 'Save Changes'}
                                    </Button>
                                </Grid2>
                            </Grid2>
                        );
                    }}
                </Form>
            </Card>
        </Grid2>
    );
};
